import React, { useCallback, useEffect, useState } from "react";
import { toast } from "Components/DesignSystem/Toast/useToast";
// release-sept-29 merge (2026-09-15): react-toastify is no longer a dependency — the app has one
// toast system (design-system § 116). Same calls, same messages; they land in the DS ToastRegion.
import StrategyCard from "./StrategyCard";
import StrategyPreviewDrawer from "./StrategyPreviewDrawer";
import { getCommendStrategies, previewStrategy } from "../Commend3Services/Commend3Services";
import { Commend3PreviewProduct, Commend3Strategy } from "../Types";
import {
  AUTO_REFRESH_INTERVAL_MS,
  STRATEGIES_INFO_NOTE,
  STRATEGIES_SECTION_TITLE,
} from "../Commend3Constants";

type StrategiesTabProps = {
  channelKey: string;
  generateEnabled: boolean;
  generateUnavailableReason?: string;
  onGenerate?: (strategy: Commend3Strategy) => void;
};

const StrategiesTab = ({
  channelKey,
  generateEnabled,
  generateUnavailableReason,
  onGenerate,
}: StrategiesTabProps) => {
  const [strategies, setStrategies] = useState<Commend3Strategy[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [previewTarget, setPreviewTarget] = useState<Commend3Strategy | null>(null);

  const fetchStrategies = useCallback(
    async (silent: boolean = false) => {
      if (!channelKey) return;
      if (!silent) setLoading(true);
      try {
        const res = await getCommendStrategies(channelKey);
        if (res?.done) {
          setStrategies(res?.data ?? []);
        } else {
          toast.error(res?.message ?? "Failed to fetch strategies");
        }
      } catch (error) {
        console.error("Error fetching Commend 3.0 strategies", error);
        toast.error("Failed to fetch strategies. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [channelKey]
  );

  useEffect(() => {
    if (!channelKey) return;
    fetchStrategies();
    const intervalId = setInterval(() => fetchStrategies(true), AUTO_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [channelKey, fetchStrategies]);

  const handlePreviewSearch = useCallback(
    async (term: string): Promise<Commend3PreviewProduct | null> => {
      if (!channelKey || !previewTarget) return null;
      try {
        const res = await previewStrategy(channelKey, previewTarget.uid, term);
        if (res?.done) return res?.data ?? null;
        return null;
      } catch (error) {
        console.error("Error previewing Commend 3.0 strategy", error);
        return null;
      }
    },
    [channelKey, previewTarget]
  );

  return (
    <div
      id="commend3-strategies-tab"
      className="tw-flex tw-flex-1 tw-flex-col tw-gap-3.5 tw-overflow-y-auto tw-px-[22px] tw-py-5"
    >
      <p
        className="tw-rounded-md tw-px-[13px] tw-py-2.5 tw-text-[13px] tw-text-[#59596E]"
        style={{
          lineHeight: 1.6,
          background: "#F4F5F9",
          borderLeft: "2.5px solid #c8c6bf",
        }}
      >
        {STRATEGIES_INFO_NOTE}
      </p>

      <span
        className="tw-text-[11px] tw-font-semibold tw-uppercase tw-text-[#59596E]"
        style={{ letterSpacing: ".06em" }}
      >
        {STRATEGIES_SECTION_TITLE}
      </span>

      {loading && strategies.length === 0 ? (
        <span className="tw-text-[13px] tw-text-[#59596E]">Loading strategies…</span>
      ) : strategies.length === 0 ? (
        <span className="tw-text-[13px] tw-text-[#59596E]">
          No strategies found for this environment.
        </span>
      ) : (
        <div className="tw-flex tw-flex-col tw-gap-3.5">
          {strategies.map((strategy, index) => (
            <StrategyCard
              key={strategy.uid}
              strategy={strategy}
              index={index}
              generateEnabled={generateEnabled}
              generateUnavailableReason={generateUnavailableReason}
              onGenerate={onGenerate}
              onPreview={setPreviewTarget}
            />
          ))}
        </div>
      )}

      <StrategyPreviewDrawer
        strategy={previewTarget}
        onClose={() => setPreviewTarget(null)}
        onSearch={handlePreviewSearch}
      />
    </div>
  );
};

export default StrategiesTab;
